/**
 * CONSAI Business App - Premium Plan Thank You Page
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  CheckCircle2, Calendar, CreditCard, Crown, Users, FileText, 
  MessageSquare, Mic, BarChart3, LineChart, Sparkles, Headphones 
} from 'lucide-react';
import { trackSubscribe } from '../utils/metaPixel';

const ThankYouPremium = () => {
  const navigate = useNavigate();
  
  // Calculate next billing date (1 month from now)
  const nextBillingDate = new Date();
  nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
  const formattedDate = nextBillingDate.toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  // Fire Meta Pixel event on page load
  useEffect(() => {
    trackSubscribe('Premium Plan', 99);
  }, []);

  const features = [
    { icon: Users, text: 'Unlimited customers & invoices' },
    { icon: MessageSquare, text: 'AI Assistant (50 commands/day)' },
    { icon: Mic, text: 'Voice commands' },
    { icon: BarChart3, text: 'Advanced analytics & forecasting' },
    { icon: Sparkles, text: 'Daily AI summaries' },
    { icon: Headphones, text: 'Priority support' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_consai-foundation/artifacts/0mnri4ba_CONSAI%20Logo_compressed.png" 
            alt="CONSAI" 
            className="h-10 w-10"
          />
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-600">
            Go to Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          {/* Success Icon with Crown */}
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-14 w-14 text-purple-600" />
            </div>
            <div className="absolute -top-2 -right-2 w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
              <Crown className="h-5 w-5 text-yellow-800" />
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#021945] font-manrope mb-4">
            Welcome to Premium! 🚀
          </h1>
          <p className="text-lg text-slate-600">
            You now have full access to all Premium features. Let's grow your business!
          </p>
        </div>

        {/* Plan Details Card */}
        <Card className="mb-8 border-2 border-purple-200 bg-gradient-to-br from-white to-purple-50/50">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 font-manrope mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-600" />
              Plan Details
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-purple-100">
                <span className="text-slate-600">Plan</span>
                <span className="font-semibold text-purple-700 flex items-center gap-1">
                  <Crown className="h-4 w-4" />
                  Premium Plan
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-purple-100">
                <span className="text-slate-600">Amount Paid</span>
                <span className="font-semibold text-green-600">₹99/month</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-purple-100">
                <span className="text-slate-600">Billing</span>
                <span className="font-semibold text-slate-900">Monthly auto-renewal</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-slate-600 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Next billing
                </span>
                <span className="font-semibold text-slate-900">{formattedDate}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Premium Features Card */}
        <Card className="mb-8 bg-gradient-to-br from-purple-100/50 to-purple-50/30 border-purple-200">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-purple-900 font-manrope mb-4 flex items-center gap-2">
              <Crown className="h-5 w-5 text-purple-600" />
              Premium Features Unlocked
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <feature.icon className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-slate-700 text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA Button */}
        <div className="text-center">
          <Button 
            onClick={() => navigate('/dashboard')}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-6 text-lg shadow-lg"
            data-testid="explore-premium-btn"
          >
            <Crown className="h-5 w-5 mr-2" />
            Explore Premium Features
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-slate-500 text-sm">
        <p>© 2026 BUFFETISM IT VENTURES PVT LTD. All rights reserved</p>
        <p className="mt-2">Need help? Contact support@consai.in</p>
      </footer>
    </div>
  );
};

export default ThankYouPremium;
